/** Experimental bread model. Specification: MODELE-PLANIFICATEUR-PAIN.md, alpha 2026-09-19. */
export type BreadMode = 'idy' | 'levain' | 'hybride'
export type Flour = { name: string; grams: number }
export type Anchor = { q: number; exposure: number }
export type HybridTrial = { q: number; yeastPercent: number; yeastShape: number; yeastBake: number; starterShape: number; starterBake: number }
export type BreadOptions = {
  mode: BreadMode; flours: Flour[]; hydration: number; protein: number | null; salt: number
  mix: string; hours: number; useFridge: boolean; coldIn: number; coldOut: number
  ambient: number; fridge: number; initial: number; starterHydration: number; starterReady: boolean
  anchors: Anchor[]; hybridTrials: HybridTrial[]; hybridPff: number; sameHybridContext: boolean
  scale: number; refreshFlour: number; refreshWater: number; seedHydration: number; peakHours: number
  vigour: number
  /** Hours from mixing to shaping. `null` keeps the derived default of §8.3. */
  shape: number | null
  /** Proof at room temperature wanted in total, shaping to oven, in hours equivalent to
   * 21 °C. It may be served before the cold phase, after it, or both. */
  proofTarget: number
}
export type Interval = [number, number]
export type Exposure = {
  yeastShape: number; yeastBake: number; starterShape: number; starterBake: number
  /** Exposure reached when the refrigerator is entered, so the part of the final proof that
   * happened at room temperature can be told apart from the part that happened cold. */
  yeastCold: number; starterCold: number
  /** Exposure reached when it is taken out again: `bake - out` is the warm finish. */
  yeastOut: number; starterOut: number
  /** Proof from shaping to oven, each moment weighted by how much it actually inflates the
   * dough rather than merely ferments it. */
  yeastProof: number; starterProof: number
}
export type ThermalPoint = { time: number; temperature: number }
export type BreadPlan = {
  mode: BreadMode; status: 'SENSIBLE' | 'INCOMPATIBLE_CENTRAL' | 'HORS_DOMAINE' | 'HYBRIDE_NON_CALIBRE'
  errors: string[]; warnings: string[]; flour: number; mass: number; exposure?: Exposure; trajectory: ThermalPoint[]
  dose?: number; interval?: Interval; sensitivity?: Interval; pff?: number; starter?: number
  addedFlour?: number; addedWater?: number; salt?: number; lambda?: Interval
  calibration: string; fermentationWarning?: string; coldBakeWarning?: string; flourFit?: FlourFit; bakeTemperature?: number; warmProof?: number; proofExposure?: number; effectiveProof?: number; proofAdvice?: ProofAdvice
  verdict?: 'ok' | 'attention' | 'probleme'
  hull?: Interval
}
export type FlourFit = {
  level: 'INCONNU' | 'ADAPTE' | 'RIC_RAC' | 'RISQUE'
  label: string
  message: string
}
export const BREAD_STORAGE_KEY = 'recettes:pain-planificateur:v2'
export const BREAD_FIXED_ASSUMPTIONS = {
  salt: 2,
  ambient: 21,
  fridge: 4,
  initial: 21,
  scale: 0.1,
} as const
const B: Interval = [0.3, 0.5]
const C: Interval = [0.9, 1.1]
/** Anchor and exponent of the dose–exposure law, both least-squares fits of the TXCraig1
 * baker's yeast quantity prediction model as published through the PizzaBlab calculator
 * (§22.2), over its nineteen points between 3 and 36 h at 21 °C. Residuals then sit between
 * 0,88 and 1,09 with a mean of 1,00; pinning the curve to its 12 h point instead left the
 * whole engine about 10 % low. The alpha values were 0,05 % and 1,2: the anchor was very
 * nearly right, the exponent slightly too shallow. */
const Y0 = 0.0605
const A0 = 12
const BETA = 1 / 1.342

/** Relative vigour of the ferment, `η` in the specification (§8.1, §22).
 *
 * It stays at 1 — the community reference curve below already sets the scale, and essai n° 1
 * gave no reason to move it: the dose used that day (0,200 %) was within 7 % of what that
 * curve prescribes. It remains exposed so a baker can calibrate it from their own crumb. */
export const IDY_VIGOUR_PRIOR = 1

export const vigourEnvelope = (vigour: number): Interval => [vigour / 1.25, vigour * 1.25]

/** The smallest interval containing every envelope member: what the dose could plausibly be.
 * Distinct from `intersect`, which answers the narrower question of §8.4 — which dose would
 * still work whichever member turns out to be true. */
const hullOf = (intervals: Interval[]): Interval | undefined => intervals.length
  ? [Math.min(...intervals.map((i) => i[0])), Math.max(...intervals.map((i) => i[1]))]
  : undefined
const finite = (...values: number[]) => values.every(Number.isFinite)
const n = (value: number) => value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
const intersect = (intervals: Interval[]): Interval | undefined => {
  if (!intervals.length) return undefined
  const lo = Math.max(...intervals.map((i) => i[0]))
  const hi = Math.min(...intervals.map((i) => i[1]))
  return lo <= hi ? [lo, hi] : undefined
}

/** Starting schedules per ferment. A sourdough starter is far slower than commercial yeast,
 * especially in the cold — its activity at 4 °C is 0,039 against 0,078 — so the yeast default
 * leaves it outside the usable range of preferment fractions. Switching ferment therefore
 * switches the starting schedule rather than handing the baker an error to decipher. */
export const MODE_SCHEDULES: Record<BreadMode, Pick<BreadOptions, 'hours' | 'coldIn' | 'coldOut' | 'useFridge'>> = {
  idy: { hours: 19, coldIn: 3.5, coldOut: 19, useFridge: true },
  levain: { hours: 20, coldIn: 8, coldOut: 20, useFridge: true },
  hybride: { hours: 20, coldIn: 8, coldOut: 20, useFridge: true },
}

/** Applies a ferment's starting schedule, keeping a shaping milestone only if it still fits. */
export function switchBreadMode(o: BreadOptions, mode: BreadMode): BreadOptions {
  const next = { ...o, mode, ...MODE_SCHEDULES[mode] }
  const bounds = shapeBounds(next)
  return {
    ...next,
    coldOut: suggestedColdOut(next),
    shape: o.shape !== null && o.shape >= bounds[0] && o.shape <= bounds[1] ? o.shape : null,
  }
}

const pad = (value: number) => String(value).padStart(2, '0')
export const localDateTimeValue = (date: Date) => Number.isFinite(date.getTime())
  ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  : ''

/** Keeps the time of day, moves the calendar day to today. The planner asks for hours, not
 * dates: every milestone after the start is stored as an offset from it. */
export function anchorMixToToday(mix: string, today = new Date()): string {
  const parsed = new Date(mix)
  if (!Number.isFinite(parsed.getTime())) return mix
  return localDateTimeValue(new Date(today.getFullYear(), today.getMonth(), today.getDate(), parsed.getHours(), parsed.getMinutes()))
}

/** Splits an offset from the start into the pair the interface edits: a time of day and a
 * number of calendar days after the start. Going through the local calendar rather than
 * dividing by 24 keeps it correct across a daylight-saving change. */
export function milestoneParts(mix: string, hours: number): { time: string; day: number } {
  const base = new Date(mix)
  const at = new Date(base.getTime() + hours * 3_600_000)
  if (!Number.isFinite(at.getTime())) return { time: '', day: 0 }
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return { time: `${pad(at.getHours())}:${pad(at.getMinutes())}`, day: Math.round((midnight(at) - midnight(base)) / 86_400_000) }
}

export function milestoneHours(mix: string, time: string, day: number): number {
  const base = new Date(mix)
  const [h, m] = time.split(':').map(Number)
  if (!Number.isFinite(base.getTime()) || !Number.isFinite(h) || !Number.isFinite(m)) return NaN
  return (new Date(base.getFullYear(), base.getMonth(), base.getDate() + day, h, m).getTime() - base.getTime()) / 3_600_000
}

export function defaultBreadOptions(): BreadOptions {
  const now = new Date()
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const base: BreadOptions = {
    mode: 'idy', flours: [{ name: 'Blé T65', grams: 500 }],
    hydration: 70, protein: null, salt: BREAD_FIXED_ASSUMPTIONS.salt, mix: `${date}T17:00`, hours: 19, useFridge: true, coldIn: 3.5, coldOut: 19,
    ambient: BREAD_FIXED_ASSUMPTIONS.ambient, fridge: BREAD_FIXED_ASSUMPTIONS.fridge,
    initial: BREAD_FIXED_ASSUMPTIONS.initial, starterHydration: 100, starterReady: true,
    anchors: [], hybridTrials: [], hybridPff: 5, sameHybridContext: false,
    scale: BREAD_FIXED_ASSUMPTIONS.scale, refreshFlour: 2, refreshWater: 2, seedHydration: 100, peakHours: 0,
    vigour: IDY_VIGOUR_PRIOR, shape: null, proofTarget: PROOF_TARGET_DEFAULT,
  }
  // The loaf leaves the cold early enough to reach a workable temperature, rather than going
  // to the oven at 4 °C: essai n° 1 (§22) failed on exactly that default.
  return { ...base, coldOut: suggestedColdOut(base) }
}

/** Safely restores the editable planner state. Unknown, malformed and non-finite values are
 * ignored field by field, while the deliberately non-editable recipe assumptions stay fixed. */
export function parseStoredBreadOptions(value: string | null, defaults = defaultBreadOptions()): BreadOptions {
  if (!value) return defaults
  let raw: unknown
  try { raw = JSON.parse(value) } catch { return defaults }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return defaults
  const saved = raw as Record<string, unknown>
  const number = (key: keyof BreadOptions, min = -Infinity, max = Infinity) => typeof saved[key] === 'number' && Number.isFinite(saved[key]) && saved[key] >= min && saved[key] <= max
    ? saved[key] as number : defaults[key] as number
  const flours = Array.isArray(saved.flours) && saved.flours.length > 0
    ? saved.flours.flatMap((candidate): Flour[] => {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return []
      const flour = candidate as Record<string, unknown>
      if (typeof flour.name !== 'string' || typeof flour.grams !== 'number' || !Number.isFinite(flour.grams) || flour.grams <= 0 || flour.grams > 1200) return []
      return [{ name: flour.name.slice(0, 100), grams: flour.grams }]
    }).slice(0, 1) : defaults.flours
  const anchors = Array.isArray(saved.anchors) ? saved.anchors.flatMap((candidate): Anchor[] => {
    if (!candidate || typeof candidate !== 'object') return []
    const anchor = candidate as Record<string, unknown>
    return typeof anchor.q === 'number' && Number.isFinite(anchor.q) && typeof anchor.exposure === 'number' && Number.isFinite(anchor.exposure)
      ? [{ q: anchor.q, exposure: anchor.exposure }] : []
  }).slice(0, 2) : defaults.anchors
  const hybridTrials = Array.isArray(saved.hybridTrials) ? saved.hybridTrials.flatMap((candidate): HybridTrial[] => {
    if (!candidate || typeof candidate !== 'object') return []
    const trial = candidate as Record<string, unknown>
    const keys: (keyof HybridTrial)[] = ['q', 'yeastPercent', 'yeastShape', 'yeastBake', 'starterShape', 'starterBake']
    if (!keys.every((key) => typeof trial[key] === 'number' && Number.isFinite(trial[key]))) return []
    return [Object.fromEntries(keys.map((key) => [key, trial[key]])) as HybridTrial]
  }).slice(0, 20) : defaults.hybridTrials
  const mode = saved.mode === 'idy' || saved.mode === 'levain' || saved.mode === 'hybride' ? saved.mode : defaults.mode
  const useFridge = typeof saved.useFridge === 'boolean' ? saved.useFridge : defaults.useFridge
  let hours = number('hours', 0.75, 36)
  let coldIn = number('coldIn', 0.5, 36)
  let coldOut = number('coldOut', 0.5, 36)
  if ((useFridge && !(coldIn <= coldOut && coldOut <= hours)) || (!useFridge && hours < 2)) {
    hours = defaults.hours
    coldIn = defaults.coldIn
    coldOut = defaults.coldOut
  }
  return {
    ...defaults,
    mode,
    flours: flours.length ? flours : defaults.flours,
    hydration: number('hydration', 60, 80),
    protein: saved.protein === null ? null : number('protein', 5, 25),
    // Only the time of day is meaningful: a plan restored tomorrow should still read as
    // starting today, not as a schedule that ran yesterday.
    mix: typeof saved.mix === 'string' && Number.isFinite(new Date(saved.mix).getTime()) ? anchorMixToToday(saved.mix) : defaults.mix,
    hours,
    useFridge,
    coldIn,
    coldOut,
    starterHydration: number('starterHydration', 1, 300),
    anchors,
    hybridTrials,
    hybridPff: number('hybridPff', 0.1, 40),
    sameHybridContext: typeof saved.sameHybridContext === 'boolean' ? saved.sameHybridContext : defaults.sameHybridContext,
    refreshFlour: number('refreshFlour', 0.1, 100),
    refreshWater: number('refreshWater', 0, 100),
    seedHydration: number('seedHydration', 1, 300),
    peakHours: number('peakHours', 0, 72),
    vigour: number('vigour', 0.1, 1.5),
    proofTarget: number('proofTarget', 0, 12),
    shape: typeof saved.shape === 'number' && Number.isFinite(saved.shape) && saved.shape > 0 && saved.shape <= 36 ? saved.shape : null,
    starterReady: true,
    salt: BREAD_FIXED_ASSUMPTIONS.salt,
    scale: BREAD_FIXED_ASSUMPTIONS.scale,
    // Product decision: the only temperature worth asking a home baker for is the room.
    // Probing a dough is not something anyone does before every loaf, so the dough is assumed
    // to start at room temperature, and the refrigerator is held at its fixed assumption.
    ambient: number('ambient', 15, 28),
    fridge: BREAD_FIXED_ASSUMPTIONS.fridge,
    initial: number('ambient', 15, 28),
  }
}

/** Relative activity of baker's yeast in dough, normalised at 21 °C.
 *
 * Measured, not posited: each node is an iso-dose reading of the TXCraig1 reference model
 * (§22.2). At a given temperature the reference gives the IDY percentage needed for a
 * fermentation of `t` hours; the time at 21 °C requiring that same percentage divided by `t`
 * is the relative activity. Readings were consistent across the whole time range — for
 * instance 4 °C landed between 0,077 and 0,083 over 16 to 96 h.
 *
 * This replaces the piecewise `Q10` law of the alpha, whose cold branch was close (4,45
 * against a measured 4,4) but whose warm branch was badly wrong: it used `Q10 = 2` where the
 * reference implies roughly 4,8, so it under-reacted to a warm kitchen by a factor of 1,6
 * at 27 °C. The published chart halves the yeast for every 3 °C gained. */
const ACTIVITY_NODES: [number, number][] = [[4, 0.078], [6, 0.115], [8, 0.167], [18, 0.625], [21, 1], [24, 1.6], [27, 2.359]]

/** Solubility of CO₂ in water at 1 atm, in g/L. Physical data, used only through its ratio. */
const CO2_SOLUBILITY: [number, number][] = [[0, 3.35], [5, 2.77], [10, 2.32], [15, 1.97], [20, 1.69], [25, 1.45], [30, 1.26]]

/** How much of the gas produced at `t` inflates the dough, relative to 21 °C.
 *
 * Fermentation and expansion are two different outputs — §3.3 asks for both and the engine
 * had only ever had the first. CO₂ is about twice as soluble at 5 °C as at 25 °C, so much of
 * what is produced in the cold dissolves in the aqueous phase instead of inflating a cell,
 * and the gas already in bubbles contracts by some 8 % over that range. That is why a loaf
 * does not visibly rise in a refrigerator although it keeps fermenting. The dissolved gas is
 * not lost: it comes back out on warming and in the oven, and feeds the oven spring.
 *
 * The weight is the solubility ratio, capped at 1 since no bonus is claimed above the
 * normalisation point. It is a prior, not a derivation — the fraction of produced gas that
 * reaches the gas phase also depends on saturation, nucleation and diffusion, none of them
 * modelled here — but it has a published mechanism and a measured magnitude behind it.
 *
 * A steeper weighting, null below 8 °C, was briefly used because it flagged essai n° 1. It
 * was withdrawn: professional retarded proofing runs 12 to 36 h at 3 to 4 °C and produces
 * fully proofed loaves, so a cold proof plainly works. See §22.13 for that whole episode.
 *
 * None of this touches the dose, anchored on a reference that covers cold schedules and
 * measures fermentation — the right quantity there. This is the second output. */
export function expansionWeight(t: number): number {
  const nodes = CO2_SOLUBILITY
  const solubility = (x: number) => {
    if (x <= nodes[0][0]) return nodes[0][1]
    if (x >= nodes[nodes.length - 1][0]) return nodes[nodes.length - 1][1]
    const i = nodes.findIndex(([temperature]) => temperature > x) - 1
    const [a, sa] = nodes[i]
    const [b, sb] = nodes[i + 1]
    return Math.exp(Math.log(sa) + (x - a) / (b - a) * (Math.log(sb) - Math.log(sa)))
  }
  return Math.min(1, solubility(21) / solubility(t))
}

/** `tilt` scales the whole response away from the 21 °C normalisation point, and is the
 * single knob the sensitivity envelope turns: `tilt = 1` is the reference, 0,9 reproduces
 * the more generous cold branch of the alpha. */
export function yeastActivity(t: number, tilt = 1): number {
  if (t < 2 || t > 28) return NaN
  const first = ACTIVITY_NODES[0]
  const last = ACTIVITY_NODES[ACTIVITY_NODES.length - 1]
  let log: number
  if (t <= first[0]) log = Math.log(first[1]) + 0.19 * (t - first[0])
  else if (t >= last[0]) log = Math.log(last[1]) + 0.129 * (t - last[0])
  else {
    const i = ACTIVITY_NODES.findIndex(([temperature]) => temperature > t) - 1
    const [a, ra] = ACTIVITY_NODES[i]
    const [b, rb] = ACTIVITY_NODES[i + 1]
    log = Math.log(ra) + (t - a) / (b - a) * (Math.log(rb) - Math.log(ra))
  }
  return Math.exp(tilt * log)
}
export function starterActivity(t: number): number {
  if (t < 2 || t > 28) return NaN
  const g = (x: number) => 0.0124 * (36 - x) ** 2.981 * Math.exp(-0.3355 * (36 - x)) +
    0.5 * 0.1267 * (41 - x) ** 1.5404 * Math.exp(-0.1931 * (41 - x))
  return g(t) / g(23.9)
}
export const pToQ = (p: number, hs: number) => p * (1 + hs) / (1 - p)
export const qToP = (q: number, hs: number) => q / (1 + hs + q)
export function starterBalance(flour: number, hydration: number, salt: number, p: number, hs: number) {
  return { starter: p * flour * (1 + hs), addedFlour: flour * (1 - p), addedWater: flour * (hydration - hs * p), salt: flour * salt }
}
export function refreshBalance(mass: number, a: number, b: number, seedHydration: number) {
  const seed = mass / (1 + a + b)
  const flour = a * seed
  const water = b * seed
  const hydration = (seed * seedHydration / (1 + seedHydration) + water) / (seed / (1 + seedHydration) + flour)
  return { seed, flour, water, hydration }
}

/** Default shaping milestone of §8.3, used whenever the baker has not moved it: 15 min before
 * the refrigerator for a shaped retard, 1 h 30 before the oven for a same-day loaf. */
export const defaultShapeTime = (o: Pick<BreadOptions, 'useFridge' | 'coldIn' | 'hours'>) =>
  o.useFridge ? o.coldIn - 0.25 : Math.max(0.25, o.hours - 1.5)

/** Shaping can be moved earlier to put the final proof at room temperature *before* the cold
 * phase, which is a different and perfectly ordinary process. It stays bounded by the entry
 * into the refrigerator: shaping after it would be a bulk retard, which §8.3 requires to be a
 * profile of its own rather than the same cursor moved further. */
export const breadShapeTime = (o: Pick<BreadOptions, 'useFridge' | 'coldIn' | 'hours' | 'shape'>) =>
  o.shape === null || !Number.isFinite(o.shape) ? defaultShapeTime(o) : o.shape

export const shapeBounds = (o: Pick<BreadOptions, 'useFridge' | 'coldIn' | 'hours'>): Interval =>
  [0.25, o.useFridge ? o.coldIn : Math.max(0.25, o.hours - 0.25)]

/** One dose for the displayed prescription, ingredient list and optional dilution.
 * Keep the analytical dose only when direct weighing at the selected resolution
 * would leave the admissible interval. That case requires dilution or a finer scale.
 */
export function prescribeBread(plan: BreadPlan, options: BreadOptions): BreadPlan {
  if (plan.dose === undefined || !plan.interval) return plan
  const rounded = Math.round(plan.dose / options.scale) * options.scale
  const fits = (value: number) => value >= plan.interval![0] - 1e-9 && value <= plan.interval![1] + 1e-9
  let dose = rounded
  if (!fits(dose)) {
    dose = Math.round(plan.dose * 100 / options.scale) * options.scale / 100
    if (options.mode === 'levain' || options.scale > 0.1 || !fits(dose) || dose * 99 > plan.addedWater!) {
      return { ...plan, dose: undefined, errors: [...plan.errors, 'Cette balance ne permet pas de préparer une dose admissible, directement ni avec la dilution à 1 %. Utiliser une balance plus précise.'] }
    }
  }
  if (options.mode === 'levain') {
    const pff = dose / (plan.flour * (1 + options.starterHydration / 100))
    return { ...plan, ...starterBalance(plan.flour, options.hydration / 100, options.salt / 100, pff, options.starterHydration / 100), dose, pff }
  }
  const mass = plan.mass - plan.dose + dose
  if (mass > 1200) return { ...plan, dose: undefined, mass, status: 'HORS_DOMAINE', errors: [...plan.errors, 'La dose arrondie porte la pâte au-delà de 1 200 g. Réduire la quantité de farine.'] }
  return { ...plan, dose, mass }
}

/** Exact thermal updates; trapezoidal activity integration, steps <= 1 minute, all events inserted.
 * The central mass exponent is the lower specified sensitivity bound (1/3), an engineering
 * convention, not a measured property. Thermal mass excludes the tiny IDY addition.
 */
/** Thermal time constant of the dough, §6.1. Uncalibrated: `tauRef` is the specification's
 * central value for 850 g, and the mass exponent its lower sensitivity bound. */
export function doughTau(o: Pick<BreadOptions, 'flours' | 'hydration' | 'salt'>, tauRef = 2, exponent = 1 / 3) {
  const flour = o.flours.reduce((sum, f) => sum + f.grams, 0)
  return tauRef * (flour * (1 + o.hydration / 100 + o.salt / 100) / 850) ** exponent
}

export function integrateBread(o: BreadOptions, tauRef = 2, tilt = 1, exponent = 1 / 3) {
  const flour = o.flours.reduce((sum, f) => sum + f.grams, 0)
  const mass = flour * (1 + o.hydration / 100 + o.salt / 100)
  const tau = doughTau(o, tauRef, exponent)
  let temperature = o.initial
  let time = 0
  let yeast = 0
  let starter = 0
  let yeastExpansion = 0
  let starterExpansion = 0
  let yeastExpansionAtShape = 0
  let starterExpansionAtShape = 0
  const exposure: Exposure = { yeastShape: 0, yeastBake: 0, starterShape: 0, starterBake: 0, yeastCold: 0, starterCold: 0, yeastOut: 0, starterOut: 0, yeastProof: 0, starterProof: 0 }
  const trajectory: ThermalPoint[] = [{ time, temperature }]
  const shape = breadShapeTime(o)
  const events = [...new Set(o.useFridge ? [shape, o.coldIn, o.coldOut, o.hours] : [shape, o.hours])].sort((a, b) => a - b)
  for (const end of events) {
    while (time < end - 1e-10) {
      const dt = Math.min(1 / 60, end - time)
      const environment = o.useFridge && time >= o.coldIn - 1e-10 && time < o.coldOut - 1e-10 ? o.fridge : o.ambient
      const next = environment + (temperature - environment) * Math.exp(-dt / tau)
      yeast += dt * (yeastActivity(temperature, tilt) + yeastActivity(next, tilt)) / 2
      starter += dt * (starterActivity(temperature) + starterActivity(next)) / 2
      yeastExpansion += dt * (yeastActivity(temperature, tilt) * expansionWeight(temperature) + yeastActivity(next, tilt) * expansionWeight(next)) / 2
      starterExpansion += dt * (starterActivity(temperature) * expansionWeight(temperature) + starterActivity(next) * expansionWeight(next)) / 2
      time += dt
      temperature = next
      trajectory.push({ time, temperature })
    }
    if (end === shape) {
      exposure.yeastShape = yeast
      exposure.starterShape = starter
      yeastExpansionAtShape = yeastExpansion
      starterExpansionAtShape = starterExpansion
    }
    if (o.useFridge && end === o.coldIn) { exposure.yeastCold = yeast; exposure.starterCold = starter }
    if (o.useFridge && end === o.coldOut) { exposure.yeastOut = yeast; exposure.starterOut = starter }
  }
  exposure.yeastBake = yeast
  exposure.starterBake = starter
  exposure.yeastProof = yeastExpansion - yeastExpansionAtShape
  exposure.starterProof = starterExpansion - starterExpansionAtShape
  if (!o.useFridge) { exposure.yeastCold = yeast; exposure.starterCold = starter; exposure.yeastOut = yeast; exposure.starterOut = starter }
  return { exposure, trajectory }
}

/** Inversion of §8.1 for a point target in `C`. A lower `vigour` means a less effective
 * ferment and therefore a larger dose; the exponent makes that dependence steep. */
export function solveYeast(bake: number, vigour = IDY_VIGOUR_PRIOR): Interval | undefined {
  if (!(bake > 0) || !(vigour > 0)) return undefined
  const k: Interval = [C[0] * A0 / bake, C[1] * A0 / bake]
  return k.map((v) => Y0 * (v / vigour) ** (1 / BETA)) as Interval
}
export function requiredStarterExposure(bake: number): Interval | undefined {
  if (!(bake > 0)) return undefined
  return [bake / C[1], bake / C[0]]
}

/** Temperature the dough is judged comfortable to enter the oven at, and the temperature
 * below which the plan is reported as having no real final proof. Both are product
 * heuristics in the sense of §9.2 — deliberately coarse, never blocking, and chosen because
 * a loaf that reaches the oven near refrigerator temperature has done nearly all of its
 * proofing at an activity of about 0,08, whatever the clock says. */
export const PROOF_COMFORTABLE = 18
const PROOF_COLD = 15

/** How long the dough still needs at room temperature to reach `target`, from the first-order
 * model of §6.1. Returns undefined when the room cannot get it there at all. */
export function warmUpTime(from: number, ambient: number, target: number, tau: number): number | undefined {
  if (!(ambient > target) || from >= target) return undefined
  return tau * Math.log((ambient - from) / (ambient - target))
}

/** Share of the final proof that must have happened at room temperature for a cold oven entry
 * to be unremarkable. A product heuristic of §9.2, not a measured threshold. */
const PROOF_WARM_SHARE = 0.25

/** Proof at room temperature wanted in total, in hours equivalent to 21 °C. */
export const PROOF_TARGET_DEFAULT = 1.5

/** The whole proof, shaping to oven, in hours equivalent to 21 °C — the cold part included,
 * because a loaf does ferment in a refrigerator, only slowly. Thirteen hours at 4 °C are
 * worth about two hours at 21 °C, which is not nothing and must not be dropped from what the
 * baker is shown. */
export function proofExposure(mode: BreadMode, e: Exposure): number {
  const raw = mode === 'levain' ? e.starterBake - e.starterShape : e.yeastBake - e.yeastShape
  return Math.max(0, mode === 'levain' ? raw / starterActivity(21) : raw)
}

/** The proof that actually inflates the loaf, in hours equivalent to 21 °C: the whole proof
 * weighted moment by moment by `expansionWeight`. This is the figure the published band is
 * judged against, because that band describes a proof that visibly rises.
 *
 * It replaces a cruder proxy that counted only the exposure outside the refrigerator window.
 * That one put a step at the refrigerator door — the first half hour inside, with the dough
 * still near 20 °C, counted for nothing — and said a cold hour was worth zero rather than
 * little. See §22.13. */
export function effectiveProof(mode: BreadMode, e: Exposure): number {
  return Math.max(0, mode === 'levain' ? e.starterProof / starterActivity(21) : e.yeastProof)
}

/** Exposure of the proof that happens at room temperature, counted in hours equivalent to
 * 21 °C whatever the ferment.
 *
 * The proof runs from shaping to the oven, and its warm part can sit on either side of the
 * cold phase: before it when the loaf is shaped early, after it when it is taken out ahead of
 * baking. Both count, and they are added. An earlier version counted only the part after the
 * refrigerator, which told a baker who had already given the loaf three hours on the counter
 * to take it out in the middle of the night.
 *
 * The starter curve is normalised at 23,9 °C, so its own units are converted rather than
 * compared across scales. */
export function warmProof(mode: BreadMode, e: Exposure): number {
  const raw = mode === 'levain'
    ? (e.starterCold - e.starterShape) + (e.starterBake - e.starterOut)
    : (e.yeastCold - e.yeastShape) + (e.yeastBake - e.yeastOut)
  return Math.max(0, mode === 'levain' ? raw / starterActivity(21) : raw)
}

/** When to take the loaf out so the part of the proof spent at room temperature is worth
 * `target` hours at 21 °C.
 *
 * This is a different requirement from the size of the proof as a whole, and both matter. A
 * loaf can collect two equivalent hours of proof entirely at 4 °C — essai n° 1 did — and
 * still reach the oven dense and unreadable. The whole proof is what the schedule is judged
 * on; the warm part is what this solves for.
 *
 * Solved against the simulation rather than in closed form: the time already spent cold, the
 * descent and the climb back all count, so a loaf whose cold phase was too short to cool it
 * needs a much shorter warm-up than one sitting at 4 °C. The warm proof falls as the exit is
 * delayed, so a bisection converges. */
export function solveColdOut(o: BreadOptions, target = o.proofTarget): number | undefined {
  if (!o.useFridge || !(target > 0) || !Number.isFinite(target)) return undefined
  const at = (coldOut: number) => warmProof(o.mode, integrateBread({ ...o, coldOut }).exposure)
  // Shaping early may already have delivered the whole proof: then there is nothing to do,
  // and the loaf can go from the cold straight to the oven.
  if (at(o.hours) >= target) return o.hours
  if (!(at(o.coldIn) >= target)) return undefined
  let lo = o.coldIn
  let hi = o.hours
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2
    if (at(mid) >= target) lo = mid; else hi = mid
  }
  return lo
}

/** The same proof expressed as clock hours at the baker's own room temperature, which is what
 * published guidance quotes. At 21 °C the two numbers coincide by construction. */
export const proofAtAmbient = (warm: number, ambient: number) => warm / yeastActivity(ambient)

/** Range of final proof at room temperature reported by baking practice, in clock hours:
 * roughly one to four, most often one to three, and shorter for a warm dough. It is judged on
 * the dough, never on a clock, so it is shown as a band and never enforced. See §22.12. */
export const PROOF_PRACTICE_RANGE: Interval = [1, 3]

export type ProofAdvice = {
  actual: number
  target: number
  status: 'ok' | 'court' | 'long'
  coldIn?: number
  coldOut?: number
  shape?: number
}

/** Moves one milestone until the proof at room temperature hits `target`, against the full
 * simulation.
 * Works in either direction: the caller need not know which way the quantity runs. */
function solveMilestone(o: BreadOptions, key: 'coldIn' | 'coldOut' | 'shape', lo: number, hi: number, target: number): number | undefined {
  if (!(hi > lo)) return undefined
  const at = (x: number) => effectiveProof(o.mode, integrateBread({ ...o, [key]: x }).exposure)
  const low = at(lo)
  const high = at(hi)
  if (Math.min(low, high) > target || Math.max(low, high) < target) return undefined
  const increasing = high > low
  let a = lo
  let b = hi
  for (let i = 0; i < 48; i++) {
    const mid = (a + b) / 2
    if ((at(mid) < target) === increasing) a = mid; else b = mid
  }
  return (a + b) / 2
}

/** What to change, and to when, so the proof at room temperature matches the target.
 *
 * Three levers, each solved the same way: shape earlier or later, enter the cold later or
 * sooner, leave it sooner or later. Moving the cold entry only helps when shaping is set
 * explicitly — left automatic it follows the cold by fifteen minutes and the warm proof
 * before the refrigerator never changes. */
export function proofAdvice(o: BreadOptions, e: Exposure): ProofAdvice {
  const actual = effectiveProof(o.mode, e)
  const target = o.proofTarget
  const advice: ProofAdvice = { actual, target, status: 'ok' }
  if (!(target > 0) || !Number.isFinite(actual)) return advice
  // Judged on the part of the proof that inflates the dough, because that is what the
  // published band describes — a proof that visibly rises. The whole proof, cold included, is
  // shown beside it for context. The band is quoted in clock hours at room temperature, so
  // the comparison is made there rather than in equivalent hours.
  const clock = proofAtAmbient(actual, o.ambient)
  if (clock >= PROOF_PRACTICE_RANGE[0] && clock <= PROOF_PRACTICE_RANGE[1]) return advice
  advice.status = clock < PROOF_PRACTICE_RANGE[0] ? 'court' : 'long'
  // Aim at the bound that was missed, converted back from clock hours into the engine's unit.
  const aim = PROOF_PRACTICE_RANGE[advice.status === 'court' ? 0 : 1] * yeastActivity(o.ambient)
  advice.target = aim
  if (o.useFridge) {
    advice.coldOut = solveMilestone(o, 'coldOut', o.coldIn, o.hours, aim)
    if (o.shape !== null) advice.coldIn = solveMilestone(o, 'coldIn', Math.max(0.25, o.shape), o.coldOut, aim)
  }
  advice.shape = solveMilestone(o, 'shape', 0.25, shapeBounds(o)[1], aim)
  return advice
}

/** The exit offered by the interface: the solved one, or the oven when no exit can deliver
 * the wanted proof — baking straight from the cold, with the proof warning to explain it. */
export function suggestedColdOut(o: BreadOptions): number {
  if (!o.useFridge) return o.hours
  return solveColdOut(o) ?? o.hours
}

/** Reports a plan whose loaf reaches the oven cold *and* never proved at room temperature.
 *
 * Baking straight from the refrigerator is an ordinary technique, and shaping early so the
 * loaf proves at room temperature *before* the cold phase is an ordinary process. What fails
 * is the combination of a short bulk, a proof spent entirely near 4 °C and a cold oven entry:
 * the whole final proof then runs at an activity of about 0,08, the loaf never visibly
 * expands, and the baker gets no cue. Essai n° 1 (§22) is exactly that case. */
function finalProofWarning(o: BreadOptions, e: Exposure, bakeTemperature: number, tau: number): string | undefined {
  if (!o.useFridge || bakeTemperature >= PROOF_COLD) return undefined
  const shape = o.mode === 'levain' ? e.starterShape : e.yeastShape
  const cold = o.mode === 'levain' ? e.starterCold : e.yeastCold
  const bake = o.mode === 'levain' ? e.starterBake : e.yeastBake
  const proof = bake - shape
  if (proof > 0 && (cold - shape) / proof >= PROOF_WARM_SHARE) return undefined
  const rounded = bakeTemperature.toLocaleString('fr-FR', { maximumFractionDigits: 0 })
  const needed = warmUpTime(bakeTemperature, o.ambient, PROOF_COMFORTABLE, tau)
  const extra = needed === undefined
    ? 'ta pièce est trop fraîche pour la réchauffer'
    : `il faudrait environ ${formatHours(needed)} de plus hors du réfrigérateur pour qu’elle approche ${PROOF_COMFORTABLE} °C`
  return `Ton pain entrerait dans le four vers ${rounded} °C. À cette température la levure travaille environ douze fois moins vite qu’à 21 °C : l’apprêt a beau durer toute la nuit, il se fait presque à l’arrêt. ${extra.charAt(0).toUpperCase()}${extra.slice(1)}. Cuire une pâte froide reste une technique valable, mais il faut alors avoir façonné plus tôt pour laisser le pain lever à température **avant** le réfrigérateur.`
}

const formatHours = (hours: number) => {
  const total = Math.round(hours * 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  return h && m ? `${h} h ${String(m).padStart(2, '0')}` : h ? `${h} h` : `${m} min`
}

function shapingWarning(shape: number, bake: number): string | undefined {
  if (!(shape > 0 && bake > shape)) return undefined
  const share = shape / bake
  if (share < B[0] / C[1]) {
    return 'La dose est calculée pour la cuisson, mais la pâte risque d’être encore peu avancée au façonnage : le froid arrive tôt par rapport au reste du protocole.'
  }
  if (share > B[1] / C[0]) {
    return 'La dose est calculée pour la cuisson, mais la pâte risque d’être déjà très avancée au façonnage : surveille-la et envisage de la mettre au froid plus tôt.'
  }
  return undefined
}

/** How long a flour tolerates fermenting, in hours equivalent to 21 °C, before it starts to
 * slacken. A product heuristic in the sense of §9.2, not a result.
 *
 * Only an upper bound. An earlier version also imposed a *minimum* duration per protein band,
 * on the reasoning that a strong flour needs time to develop extensibility. That was wrong
 * twice over: §9.1 forbids exactly this inference — «W n'est pas une durée de fermentation» —
 * and it runs backwards. Strength is tolerance: a strong flour withstands a long ferment,
 * and a short one costs it flavour, not structure. It is the weak flour that must be baked
 * before it goes. Nothing about a short schedule is a flour problem. */
export function flourTolerance(protein: number | null): number | undefined {
  if (protein === null || !finite(protein)) return undefined
  return protein < 11 ? 9 : protein < 12 ? 14 : 20
}

/** Wall-clock duration weighted for the cold, the proxy for how long the gluten is exposed to
 * its own enzymes. An hour at 4 °C counts 0,35 warm hours: much more than the 0,10 the yeast
 * gets, because proteolysis slows far less than fermentation does. Another §9.2 heuristic. */
export const flourLoad = (o: Pick<BreadOptions, 'useFridge' | 'coldIn' | 'coldOut' | 'hours'>) =>
  o.useFridge ? o.coldIn + (o.hours - o.coldOut) + (o.coldOut - o.coldIn) * 0.35 : o.hours

/** Coarse verdict on the flour, kept entirely separate from the biological dose. Judged on
 * whichever of the two proxies is less forgiving, so neither a very long cold retard nor a
 * very warm one slips through. */
export function assessFlour(protein: number | null, exposure: number, hydration: number, load = NaN): FlourFit {
  const tolerance = flourTolerance(protein)
  if (tolerance === undefined || !finite(exposure)) return {
    level: 'INCONNU',
    label: 'Tenue de la farine inconnue',
    message: 'Le taux de protéines est facultatif. Sans lui, la dose reste calculable mais la tenue de la pâte n’est pas estimée.',
  }
  const percent = protein!.toLocaleString('fr-FR')
  const slack = 1 - Math.max(0, hydration - 70) * 0.02
  const comfortable = protein! < 11 ? 8 : protein! < 12 ? 12 : 18
  // Both proxies are scored on the same scale, 1 being the comfortable bound of each.
  const score = Math.max(exposure / (tolerance * slack), finite(load) ? load / (comfortable * slack) : 0)
  if (score <= 1) return {
    level: 'ADAPTE',
    label: 'Farine a priori adaptée',
    message: `Repère indicatif pour ${percent} % de protéines : ${n(exposure)} h équivalentes à 21 °C, sous les ${n(tolerance)} h que cette farine paraît tolérer. Une fermentation courte ne lui pose pas de problème de tenue ; elle coûte du goût, pas de la structure.`,
  }
  if (score <= 1.5) return {
    level: 'RIC_RAC',
    label: 'Farine possiblement ric-rac',
    message: `Repère indicatif pour ${percent} % de protéines : ${n(exposure)} h équivalentes à 21 °C sur ${n(load)} h de repos pondérées. C’est au bord de ce que cette farine paraît tolérer ; surveille la tenue, surtout à forte hydratation.`,
  }
  return {
    level: 'RISQUE',
    label: 'Repos possiblement trop long',
    message: `Repère indicatif pour ${percent} % de protéines : ${n(exposure)} h équivalentes à 21 °C sur ${n(load)} h de repos pondérées, au-delà de ce que cette farine paraît tolérer. La pâte risque de perdre de la tenue ou de s’affaisser. Les protéines seules ne mesurent toutefois pas la force d’une farine.`,
  }
}

export function starterModel(anchors: Anchor[]) {
  if (!anchors.length) return { at: (q: number) => -5.368 * Math.log(q) - 0.6014, inverse: (r: number) => Math.exp(-(r + 0.6014) / 5.368) }
  if (anchors.length > 2 || anchors.some((a) => !finite(a.q, a.exposure) || a.q < 0.01 || a.q > 0.4 || a.exposure <= 0)) return undefined
  const first = anchors[0]
  const b = anchors.length === 2 ? (first.exposure - anchors[1].exposure) / Math.log(anchors[1].q / first.q) : 5.368
  if (!(b > 0) || !Number.isFinite(b)) return undefined
  return { at: (q: number) => first.exposure + b * Math.log(first.q / q), inverse: (r: number) => first.q * Math.exp((first.exposure - r) / b) }
}
export function hybridCoupling(trials: HybridTrial[], model: NonNullable<ReturnType<typeof starterModel>>): Interval | undefined {
  if (trials.length < 2) return undefined
  const intervals: Interval[] = []
  for (const trial of trials) {
    if (!finite(...Object.values(trial)) || trial.q < 0.01 || trial.q > 0.4 || trial.yeastPercent < 0 || trial.yeastShape <= 0 || trial.yeastBake <= trial.yeastShape || trial.starterShape <= 0 || trial.starterBake <= trial.starterShape) return undefined
    const r = model.at(trial.q)
    if (!(r > 0)) return undefined
    for (const [ay, al, band] of [[trial.yeastShape, trial.starterShape, B], [trial.yeastBake, trial.starterBake, C]] as [number, number, Interval][]) {
      const zl = al / r
      const zy = (trial.yeastPercent / Y0) ** BETA * ay / A0
      if (zy === 0) { if (zl < band[0] || zl > band[1]) return undefined; continue }
      intervals.push([Math.max(0, band[0] - zl) / zy, (band[1] - zl) / zy])
    }
  }
  const result = intersect(intervals)
  return result && result[0] > 0 && finite(...result) ? result : undefined
}
export function solveHybrid(e: Exposure, q: number, model: NonNullable<ReturnType<typeof starterModel>>, lambda: Interval): Interval | undefined {
  const r = model.at(q)
  if (!(r > 0)) return undefined
  const intervals: Interval[] = []
  for (const l of lambda) {
    for (const [ay, al, band] of [[e.yeastShape, e.starterShape, B], [e.yeastBake, e.starterBake, C]] as [number, number, Interval][]) {
      const zl = al / r
      const d = l * ay / A0
      if (band[1] < zl || !(d > 0)) return undefined
      intervals.push([Y0 * (Math.max(0, band[0] - zl) / d) ** (1 / BETA), Y0 * ((band[1] - zl) / d) ** (1 / BETA)])
    }
  }
  return intersect(intervals)
}

/** Every early return below leaves the plan incomplete on purpose; the verdict is applied
 * once, on the way out, so no exit path can forget it. */
export function calculateBread(o: BreadOptions): BreadPlan {
  const plan = computeBread(o)
  plan.verdict = breadVerdict(plan)
  return plan
}

function computeBread(o: BreadOptions): BreadPlan {
  const flour = o.flours.reduce((sum, f) => sum + f.grams, 0)
  const mass = flour * (1 + o.hydration / 100 + o.salt / 100)
  const errors: string[] = []
  const warnings = ['Dose indicative issue d’un modèle expérimental : observe toujours la pâte.']
  const result: BreadPlan = { mode: o.mode, status: 'HORS_DOMAINE', errors, warnings, flour, mass, trajectory: [], calibration: o.mode === 'idy'
      ? (Math.abs(o.vigour - IDY_VIGOUR_PRIOR) < 1e-9 ? 'Prior recalé sur un seul essai' : 'Calibration personnelle')
      : o.anchors.length ? 'Calibration personnelle ponctuelle' : 'Prior communautaire' }
  if (!finite(flour, mass, o.hydration, o.salt, o.hours, o.coldIn, o.coldOut, o.ambient, o.fridge, o.initial, o.starterHydration, o.scale) || !o.flours.length || o.flours.some((f) => !Number.isFinite(f.grams) || f.grams <= 0)) errors.push('Renseigner une quantité de farine valide et positive.')
  if (o.flours.some((f) => !f.name.trim()) || (o.protein !== null && (!finite(o.protein) || o.protein < 5 || o.protein > 25))) errors.push('Nommer la farine ; le taux de protéines doit être compris entre 5 et 25 % s’il est renseigné.')
  if (!Number.isFinite(new Date(o.mix).getTime())) errors.push('Renseigner la date et l’heure du mélange.')
  const validTiming = o.useFridge
    ? o.coldIn >= 0.5 && o.coldIn <= o.coldOut && o.coldOut <= o.hours
    : o.hours >= 2
  if (!(validTiming && o.hours <= 36)) errors.push(o.useFridge
    ? 'Ordre requis : mélange < entrée au froid ≤ sortie du froid ≤ cuisson, sur 36 h maximum.'
    : 'La cuisson doit être prévue entre 2 et 36 heures après le mélange.')
  if (mass < 500 || mass > 1200) errors.push('La masse de pâte doit rester entre 500 et 1 200 g (levain inclus).')
  if (o.hydration < 60 || o.hydration > 80 || o.salt < 1.5 || o.salt > 2.5) errors.push('Domaine : hydratation de 60 à 80 %, sel de 1,5 à 2,5 %.')
  if (o.ambient < 15 || o.ambient > 28 || o.fridge < 2 || o.fridge > 8 || o.initial < 2 || o.initial > 28) errors.push('Domaine : pièce 15–28 °C, réfrigérateur 2–8 °C, pâte initiale 2–28 °C.')
  if (!(o.scale > 0)) errors.push('La précision de la balance doit être positive.')
  const bounds = shapeBounds(o)
  if (o.shape !== null && (!finite(o.shape) || o.shape < bounds[0] - 1e-9 || o.shape > bounds[1] + 1e-9)) errors.push(o.useFridge
    ? 'Le façonnage doit tomber entre 15 minutes après le mélange et l’entrée au réfrigérateur.'
    : 'Le façonnage doit tomber entre 15 minutes après le mélange et 15 minutes avant la cuisson.')
  if (o.mode === 'idy' && (!finite(o.vigour) || o.vigour < 0.1 || o.vigour > 1.5)) errors.push('La vigueur du ferment doit rester entre 0,10 et 1,50.')
  if (o.mode !== 'idy' && (!(o.starterHydration > 0) || !o.starterReady)) errors.push('Le levain doit avoir une hydratation connue et être actif, proche du pic.')
  if (o.mode !== 'idy' && (!finite(o.refreshFlour, o.refreshWater, o.seedHydration, o.peakHours) || o.refreshFlour <= 0 || o.refreshWater < 0 || o.seedHydration <= 0 || o.peakHours < 0)) errors.push('Rafraîchi : farine et hydratation de graine positives ; eau et temps au pic non négatifs (0 = inconnu).')
  const model = starterModel(o.anchors)
  if (o.mode !== 'idy' && !model) errors.push('Calibration invalide : un ou deux ancrages quantitatifs comparables, q entre 0,01 et 0,40, expositions positives et pente positive.')
  if (errors.length) return result
  const central = integrateBread(o)
  result.exposure = central.exposure
  result.trajectory = central.trajectory
  if (o.mode !== 'idy') warnings.push('Levain au froid : extrapolation critique. La variété du levain, la courbe thermique et les résidus ne sont pas quantifiés.')
  // Judged on the exposure the engine already computes, so the figure shown to the baker and
  // the figure the verdict uses are the same one.
  result.flourFit = assessFlour(o.protein, central.exposure.yeastBake, o.hydration, flourLoad(o))
  // Baking straight out of the refrigerator is the default of the interface and the riskiest
  // configuration. Essai n° 1 (§22) failed here with a correct dose, so the plan is judged on
  // the simulated dough temperature at the oven rather than on whether a warm-up was entered
  // at all: twenty minutes out of the fridge is no more a final proof than none.
  result.bakeTemperature = central.trajectory.at(-1)?.temperature
  result.warmProof = warmProof(o.mode, central.exposure)
  result.proofExposure = proofExposure(o.mode, central.exposure)
  result.effectiveProof = effectiveProof(o.mode, central.exposure)
  result.proofAdvice = proofAdvice(o, central.exposure)
  if (result.bakeTemperature !== undefined) {
    result.coldBakeWarning = finalProofWarning(o, central.exposure, result.bakeTemperature, doughTau(o))
  }
  let interval: Interval | undefined
  let q: number | undefined
  let lambda: Interval | undefined
  const e = central.exposure
  if (o.mode === 'idy') {
    interval = solveYeast(e.yeastBake, o.vigour)
    result.fermentationWarning = o.useFridge ? shapingWarning(e.yeastShape, e.yeastBake) : undefined
  }
  else if (o.mode === 'levain' && model) {
    const r = requiredStarterExposure(e.starterBake)
    if (r) { q = model.inverse((r[0] + r[1]) / 2); interval = [model.inverse(r[1]), model.inverse(r[0])] }
    result.fermentationWarning = o.useFridge ? shapingWarning(e.starterShape, e.starterBake) : undefined
  } else if (model) {
    lambda = hybridCoupling(o.hybridTrials, model)
    if (!lambda || !o.sameHybridContext) {
      result.status = 'HYBRIDE_NON_CALIBRE'
      errors.push('Pas encore de dose : ce mode a besoin d’au moins deux de tes fournées au levain + levure pour mesurer comment tes deux ferments se combinent. Renseigne-les dans « Calibrer le mélange levain + levure » ci-dessus, puis confirme qu’elles partagent le même contexte.')
      return result
    }
    q = pToQ(o.hybridPff / 100, o.starterHydration / 100)
    if (!finite(q) || q < 0.01 || q > 0.4) { errors.push('La fraction préfermentée donne un q hors du domaine 0,01–0,40.'); return result }
    interval = solveHybrid(e, q, model, lambda)
    result.lambda = lambda
  }
  if (!interval) {
    result.status = 'INCOMPATIBLE_CENTRAL'
    errors.push(o.mode === 'hybride'
      ? 'Aucune dose hybride ne satisfait ce planning expérimental.'
      : 'Le modèle n’a pas pu calculer de dose pour la cuisson avec ces horaires.')
    return result
  }
  if (q !== undefined && (!(q >= 0.01 && q <= 0.4) || !finite(q))) {
    // Never clamp onto a bound: say which way the plan is out of range and what fixes it.
    const share = Math.round(qToP(Math.min(Math.max(q, 0.001), 5), o.starterHydration / 100) * 100)
    result.status = 'HORS_DOMAINE'
    errors.push(q > 0.4
      ? `Ce planning est trop court pour du levain : il faudrait environ ${share} % de levain rapporté à la farine, au-delà de la limite de 40 % au-dessus de laquelle le bilan farine-eau du levain domine la recette. Le levain fermente environ deux fois moins vite que la levure au froid, et le réfrigérateur ne le fait presque pas avancer. Allonge la phase à température ambiante avant le froid, ou recule la cuisson.`
      : 'Ce planning est trop long pour du levain : il en faudrait si peu que la dose ne serait ni pesable ni fiable. Raccourcis la phase à température ambiante ou avance la cuisson.')
    return result
  }
  // Restrict the displayed admissible interval, never clamp an invalid central dose.
  if (o.mode === 'levain') interval = intersect([interval, [0.01, 0.4]])!
  result.status = 'SENSIBLE'
  const p = q === undefined ? 0 : qToP(q, o.starterHydration / 100)
  const balance = starterBalance(flour, o.hydration / 100, o.salt / 100, p, o.starterHydration / 100)
  if (balance.addedWater < 0) { result.status = 'HORS_DOMAINE'; errors.push('Le levain apporte plus d’eau que la recette cible.'); return result }
  Object.assign(result, balance)
  result.pff = p
  result.interval = o.mode === 'levain' ? interval.map((v) => qToP(v, o.starterHydration / 100) * flour * (1 + o.starterHydration / 100)) as Interval : interval.map((v) => v * flour / 100) as Interval
  result.dose = o.mode === 'levain' ? balance.starter : Math.sqrt(interval[0] * interval[1]) * flour / 100
  if (o.mode !== 'levain') result.mass += result.dose
  if (result.mass > 1200) {
    result.status = 'HORS_DOMAINE'
    errors.push('L’ajout d’IDY porte la masse totale au-delà de 1 200 g. Réduire la quantité de farine.')
    result.dose = undefined
    result.interval = undefined
    return result
  }
  const envelopes: Interval[] = [result.interval]
  let feasibleEverywhere = true
  // The yeast branch also sweeps the vigour, which is the least constrained parameter of the
  // whole model: a single falsified bake bounds it from one side only.
  const vigours = o.mode === 'idy' ? vigourEnvelope(o.vigour) : [0]
  for (const tau of [1.5, 3]) for (const tilt of [0.9, 1.1]) for (const exponent of [1 / 3, 2 / 3]) for (const vigour of vigours) {
    const x = integrateBread(o, tau, tilt, exponent).exposure
    let i: Interval | undefined
    if (o.mode === 'idy') i = solveYeast(x.yeastBake, vigour)?.map((v) => v * flour / 100) as Interval | undefined
    else if (o.mode === 'levain' && model) {
      const r = requiredStarterExposure(x.starterBake)
      if (r) {
        const qs: Interval = [model.inverse(r[1]), model.inverse(r[0])]
        const domain = intersect([qs, [0.01, 0.4]])
        if (domain) i = domain.map((v) => qToP(v, o.starterHydration / 100) * flour * (1 + o.starterHydration / 100)) as Interval
      }
    } else if (model && lambda && q !== undefined) i = solveHybrid(x, q, model, lambda)?.map((v) => v * flour / 100) as Interval | undefined
    if (i) envelopes.push(i); else feasibleEverywhere = false
  }
  if (feasibleEverywhere) result.sensitivity = intersect(envelopes)
  // What the dose could plausibly be, as opposed to the narrow band around the central case.
  // Displaying this is the point: the previous interval claimed +/- 12 % on a quantity whose
  // real uncertainty is a factor of two or more.
  result.hull = hullOf(envelopes)
  if (o.mode === 'hybride') {
    if (!result.sensitivity) {
      result.dose = undefined
      errors.push('Aucune dose hybride ne convient à ce planning. Modifie les horaires ou choisis un seul ferment.')
      return result
    }
    result.interval = result.sensitivity
    result.dose = Math.sqrt(result.interval[0] * result.interval[1])
    result.mass = mass + result.dose
    if (result.mass > 1200) { result.status = 'HORS_DOMAINE'; result.dose = undefined; errors.push('La dose hybride porte la pâte au-delà de 1 200 g.'); return result }
  }
  if (result.dose < o.scale && o.mode !== 'levain') warnings.push('Dose inférieure à la précision de la balance : utiliser une dilution immédiate.')
  return result
}

/** One colour for the whole protocol, so the schedule can be judged without reading prose.
 * Red is reserved for a plan that produces no dose at all; everything the engine flags but
 * still costs only a crumb is amber. */
export function breadVerdict(plan: BreadPlan): 'ok' | 'attention' | 'probleme' {
  if (plan.errors.length || plan.dose === undefined) return 'probleme'
  const flour = plan.flourFit?.level
  const off = plan.proofAdvice && plan.proofAdvice.status !== 'ok'
  if (plan.coldBakeWarning || plan.fermentationWarning || off || flour === 'RISQUE') return 'attention'
  return 'ok'
}
